/** Words for receipts being open or locked for members to mark what was theirs. */

// ---- Receipts page, on each receipt
/** Shown on a receipt members can still mark. Empty hides it. */
export const RECEIPT_OPEN_TEXT = "Open";
/** Shown on a locked receipt. Empty hides it. */
export const RECEIPT_LOCKED_TEXT = "Locked";
/** Button that locks an open receipt. */
export const RECEIPT_LOCK_BUTTON = "Lock";
/** Button that opens a locked receipt again. */
export const RECEIPT_OPEN_BUTTON = "Open";
/** Pop-up after locking ({name} = the receipt). */
export const RECEIPT_LOCKED_DONE = "Locked {name}";
/** Pop-up after opening ({name} = the receipt). */
export const RECEIPT_OPENED_DONE = "Opened {name}";

// ---- Ledger, for a member
/** Pop-up when a member taps an item on a locked receipt. */
export const RECEIPT_LOCKED_TOAST = "That receipt is locked.";

// ---- Receipts page, scanning a photo
/** While a photo is being opened (and an iPhone photo converted). Empty hides it. */
export const SCAN_OPENING_TEXT = "Opening the photo…";
/** While the AI finishes something else you asked first. Empty hides it. */
export const SCAN_WAITING_TEXT = "Waiting for the AI to finish what you asked first…";
/** While the AI reads the receipt. Empty hides it. */
export const SCAN_READING_TEXT = "Reading the receipt…";
/** The fold-out list of every step of the last scan, to copy when something goes wrong. Empty hides it. */
export const SCAN_LOG_TITLE = "Scan log";
/** Button that copies the scan log. */
export const SCAN_LOG_COPY = "Copy";
/** Pop-up after copying the scan log. */
export const SCAN_LOG_COPIED = "Copied the scan log";
/** How long converting an iPhone photo may take before the scan gives up, in seconds. */
export const SCAN_CONVERT_LIMIT_SECONDS = 90;
/** Pop-up when a scan finishes while the Receipts page isn't open ({store} = the store read). */
export const SCAN_READY_TOAST = "{store} receipt read. Open Receipts to check and add it.";

// ---- Receipts page, scanning: the box and its button
/** Title of the scanning box. Empty hides it. */
export const SCAN_TITLE = "Scan receipts";
/** Button that picks one or more photos (each can show one or more receipts). */
export const SCAN_BUTTON = "Scan receipts";
/** Button that stops reading (and leaves the photos still to read). */
export const SCAN_STOP = "Stop";
/** Which photo is being read when several were picked ({n}, {of}). Empty hides it. */
export const SCAN_PHOTO_PROGRESS = "Photo {n} of {of}";
/** Before a photo's own problem when several were picked ({n} = which photo, {name} = its file name). */
export const SCAN_PHOTO_FAILED = "Photo {n} ({name}): ";
/** The fold-out with the model's thinking while it reads. Empty hides it. */
export const SCAN_THINKING_TITLE = "Thinking";
/** Read aloud for the photo picker. */
export const SCAN_PICKER_LABEL = "Receipt photos";

// ---- Receipts page, scanning: the receipts waiting to be checked
/** How many receipts are waiting, shown when there's more than one ({n}). Empty hides it. */
export const SCAN_QUEUE_TEXT = "{n} receipts to check";
/** Which receipt in its photo this is, shown when the photo had more than one ({place}, {of}). Empty hides it. */
export const SCAN_PLACE_TEXT = "Receipt {place} of {of} in this photo";
/** A waiting receipt in the list ({store}, {date}, {n} = rows, {rows} = "row" or "rows"). */
export const SCAN_QUEUE_ITEM = "{store} {date}, {n} {rows}";
/** Button that cancels every receipt waiting, shown when there's more than one. */
export const SCAN_CANCEL_ALL = "Cancel all";
/** Pop-up after cancelling them all ({n}). */
export const SCAN_CANCELLED_ALL = "Cancelled {n} receipts";

// ---- Receipts page, scanning: checking one receipt
/** Read aloud for the photo beside the rows. */
export const SCAN_PHOTO_ALT = "The receipt";
export const SCAN_STORE_LABEL = "Store";
export const SCAN_DATE_LABEL = "Date";
export const SCAN_RECEIPT_LABEL = "Receipt";
/** Placeholder in the Receipt box. Empty hides it. */
export const SCAN_RECEIPT_PLACEHOLDER = "Optional, like 2";
export const SCAN_CARD_LABEL = "Card";
export const SCAN_TOTAL_LABEL = "Printed total";
/** Column titles over the rows. */
export const SCAN_COL_ITEM_NO = "Item #";
export const SCAN_COL_NAME = "Name";
export const SCAN_COL_PRICE = "Price";
export const SCAN_COL_COUPON = "Coupon";
export const SCAN_COL_CRV = "CRV";
export const SCAN_COL_TAX = "Tax";
/** Read aloud for a row's tick box ({name}). */
export const SCAN_ROW_KEEP_LABEL = "Add {name}";
/** Read aloud for the item number box. */
export const SCAN_ROW_ITEM_NO_LABEL = "Item number";
/** Read aloud for the tax choice. */
export const SCAN_ROW_TAX_LABEL = "Taxed?";
/** The three tax choices on a row. */
export const SCAN_TAX_PRESET = "Preset";
export const SCAN_TAX_YES = "Taxed";
export const SCAN_TAX_NO = "No tax";
/** Under the rows ({n} = rows ticked, {items} = "item" or "items", {cents} = their sum before tax). */
export const SCAN_ROWS_SUM = "{n} {items}, {cents} before tax";
/** Added when the receipt printed a subtotal ({cents}). */
export const SCAN_SUBTOTAL = "; the receipt's subtotal is {cents}";
/** Added when the rows add up to that subtotal. Empty hides it. */
export const SCAN_SUBTOTAL_MATCHES = ", it matches";
/** Shown when the rows were changed from what was read. Empty hides it. */
export const SCAN_ROWS_CHANGED = "(You changed some rows.)";
/** Before the people chips. Empty hides it. */
export const SCAN_SPLIT_LABEL = "Split between";
/** Button that adds the receipt ({n} = rows, {items} = "item" or "items"). */
export const SCAN_ADD_BUTTON = "Add {n} {items}";
/** Button that cancels this receipt (the next one waiting opens). */
export const SCAN_CANCEL = "Cancel";
/** Pop-up after adding ({n} = items, {items} = "item" or "items"). */
export const SCAN_ADDED_TOAST = "Added {n} {items} from the receipt";

// ---- Receipts page, without your AI: copy the prompt to any AI chat, paste its JSON back
/** Button beside Scan receipts that copies the prompt for any AI chat (ChatGPT, Claude, Gemini). Empty hides it. */
export const PASTE_COPY_PROMPT_BUTTON = "Copy prompt";
/** Pop-up after copying the prompt. */
export const PASTE_PROMPT_COPIED = "Copied the receipt prompt";
/** Pop-up when the browser won't copy (the prompt opens in the paste box to copy by hand). */
export const PASTE_PROMPT_COPY_FAILED =
  "Couldn't copy here. The prompt is in the box below to copy by hand.";
/** Button beside Scan receipts that opens the box to paste the AI's reply. Empty hides it. */
export const PASTE_BUTTON = "Paste JSON";
/** Read aloud for the paste box, and its title. */
export const PASTE_LABEL = "The AI's reply";
/** Placeholder in the paste box. Empty hides it. */
export const PASTE_PLACEHOLDER = '{"receipts":[…]}';
/** Button that reads the pasted reply. */
export const PASTE_READ_BUTTON = "Read";
/** Button that closes the paste box. */
export const PASTE_CLOSE_BUTTON = "Close";
/** When the pasted text has no receipt with items in it. */
export const PASTE_NOTHING =
  "No receipt with items in what you pasted. Paste the AI's whole reply, the JSON included.";
/** Pop-up after reading ({n} = receipts, {receipts} = "receipt" or "receipts"). */
export const PASTE_READ_TOAST = "Read {n} {receipts}. Check and add below.";
/** Shown on a receipt that came from a paste, where a scan shows its photo. Empty hides it. */
export const PASTE_SOURCE_TEXT = "Pasted";
